<script setup lang="ts">
import { useEventListener, useTimeoutFn } from '@vueuse/core';
import { computed, ref, watch, type CSSProperties } from 'vue';

import { useFloatingPosition, useIsMobile } from '@/shared/lib/composables';
import { IconChevronDown, IconChevronLeft } from '@/shared/ui/icons';
import { OverlayTeleport } from '@/shared/ui/overlay';

import type { DropdownMenuItem, DropdownContentConfig } from './types';

import { useDropdown } from './composables/use-dropdown';
import DropdownItemButton from './DropdownItemButton.vue';

interface Props {
  label?: string;
  items?: DropdownMenuItem[];
  content?: DropdownContentConfig;
  width?: string;
  /** Жёсткий потолок высоты поповера. Превалирует над `content.maxHeight`. */
  maxHeight?: number;
  showChevron?: boolean;
  disabled?: boolean;
  /** Дополнительный класс на обёртку триггера (используется как floating-ui anchor). */
  triggerClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  items: undefined,
  content: undefined,
  width: 'auto',
  maxHeight: undefined,
  showChevron: true,
  disabled: false,
  triggerClass: '',
});

const resolvedMaxHeight = computed(() => props.maxHeight ?? props.content?.maxHeight);

const { isOpen, triggerRef, floatingRef, dropdownStyles, transformOrigin, toggle, close } = useDropdown({
  offset: props.content?.sideOffset ?? 8,
  align: props.content?.align,
  side: props.content?.side,
  flip: props.content?.flip,
  shift: props.content?.shift,
  maxHeight: resolvedMaxHeight.value,
});

const { isMobile } = useIsMobile();

/** Пункт, чьё подменю открыто: на компьютере сбоку, на телефоне вместо списка. */
const submenuItem = ref<DropdownMenuItem | null>(null);
const isSubmenuOpen = computed(() => submenuItem.value !== null && !isMobile.value);
const {
  reference: submenuAnchorRef,
  floating: submenuRef,
  dropdownStyles: submenuStyles,
} = useFloatingPosition({ open: isSubmenuOpen, placement: 'right-start', offset: 4 });
void submenuRef.value;

const closeSubmenu = () => {
  submenuItem.value = null;
};

// Мышь пересекает зазор между пунктом и подменю: закрываем с задержкой.
const { start: scheduleSubmenuClose, stop: cancelSubmenuClose } = useTimeoutFn(closeSubmenu, 150, { immediate: false });

const openSubmenu = (item: DropdownMenuItem, event: Event) => {
  cancelSubmenuClose();
  submenuAnchorRef.value = event.currentTarget as HTMLElement;
  submenuItem.value = item;
};

const handleItemHover = (item: DropdownMenuItem, event: Event) => {
  if (item.children && !item.disabled && !isMobile.value) openSubmenu(item, event);
};

const handleItemLeave = () => {
  if (submenuItem.value && !isMobile.value) scheduleSubmenuClose();
};

watch(isOpen, (open) => {
  if (!open) closeSubmenu();
});

useEventListener(typeof document === 'undefined' ? undefined : document, 'keydown', (event: KeyboardEvent) => {
  if (isOpen.value && event.key === 'Escape') {
    event.preventDefault();
    if (submenuItem.value) closeSubmenu();
    else close();
  }
});

// triggerRef используется в template через ref="triggerRef"
void triggerRef.value;
void floatingRef.value;

const popoverStyle = computed<CSSProperties>(() => ({
  ...dropdownStyles.value,
  width: props.width,
  transformOrigin: transformOrigin.value,
}));

/** На телефоне подменю заменяет список на месте. */
const visibleItems = computed(() => (isMobile.value ? submenuItem.value?.children : undefined) ?? props.items);

const handleItemClick = async (item: DropdownMenuItem, event: Event) => {
  if (item.disabled || item.loading) return;
  if (item.children) {
    openSubmenu(item, event);
    return;
  }
  if (!item.onClick) return;
  await item.onClick(item);
  close();
};
</script>

<template>
  <div>
    <!-- Автоматический триггер с label -->
    <button
      v-if="label"
      ref="triggerRef"
      class="flex items-center gap-2 rounded-lg bg-(--button-secondary-bg) px-4 py-2 text-(--text-primary) inset-ring inset-ring-(--button-secondary-border) transition-colors duration-200 hover:bg-(--button-secondary-hover)"
      :disabled="disabled"
      @click="toggle"
    >
      <span class="text-sm font-medium">{{ label }}</span>
      <IconChevronDown
        v-if="showChevron"
        class="transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        :width="10"
        :height="10"
      />
    </button>

    <!-- Кастомный триггер через slot -->
    <div
      v-else
      ref="triggerRef"
      :class="triggerClass"
    >
      <slot
        name="trigger"
        :is-open="isOpen"
        :toggle="toggle"
      />
    </div>

    <!-- Меню -->
    <OverlayTeleport>
      <!-- Overlay для закрытия меню при клике вне его -->
      <div
        v-if="isOpen"
        class="fixed inset-0 z-(--popup-z-index)"
        @click="close"
      />

      <!-- Выпадающее меню -->
      <Transition
        enter-active-class="transition-[opacity,scale] duration-[180ms] ease-snappy"
        enter-from-class="opacity-0 scale-[0.96] motion-reduce:scale-100"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition-opacity duration-100 ease-snappy"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen"
          ref="floatingRef"
          class="base-dropdown-popup z-(--popup-z-index) overflow-y-auto rounded-lg border border-(--border) bg-(--surface-overlay) shadow-xl"
          :style="popoverStyle"
          @click.stop
        >
          <!-- Автоматический рендер items -->
          <div
            v-if="visibleItems"
            class="py-1"
          >
            <button
              v-if="isMobile && submenuItem"
              type="button"
              class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-(--text-primary) transition-colors duration-150 hover:bg-(--surface-hover)"
              @click="closeSubmenu"
            >
              <IconChevronLeft
                :width="12"
                :height="12"
              />
              <span>{{ submenuItem.label }}</span>
            </button>
            <template
              v-for="(item, index) in visibleItems"
              :key="item.id || index"
            >
              <div
                v-if="item.divider"
                class="my-1 h-px bg-(--border)"
              />
              <DropdownItemButton
                v-else
                :item="item"
                :expanded="isSubmenuOpen && submenuItem === item"
                @click="handleItemClick(item, $event)"
                @mouseenter="handleItemHover(item, $event)"
                @mouseleave="handleItemLeave"
              />
            </template>
          </div>

          <!-- Кастомный контент через slot -->
          <slot
            v-else
            name="content"
            :close="close"
          />
        </div>
      </Transition>

      <!-- Подменю на компьютере -->
      <div
        v-if="isOpen && isSubmenuOpen && submenuItem?.children"
        ref="submenuRef"
        class="z-(--popup-z-index) overflow-y-auto rounded-lg border border-(--border) bg-(--surface-overlay) py-1 shadow-xl"
        :style="{ ...submenuStyles, width }"
        role="menu"
        :aria-label="submenuItem.label"
        @click.stop
        @mouseenter="cancelSubmenuClose"
        @mouseleave="scheduleSubmenuClose"
      >
        <template
          v-for="(child, index) in submenuItem.children"
          :key="child.id || index"
        >
          <div
            v-if="child.divider"
            class="my-1 h-px bg-(--border)"
          />
          <DropdownItemButton
            v-else
            :item="child"
            @click="handleItemClick(child, $event)"
          />
        </template>
      </div>
    </OverlayTeleport>
  </div>
</template>
